import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MessageCircleQuestion } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import SkeletonOrComponent from '@/components/molecules/SkeletonOrComponent';
import { ExecuteKimSearch, KimMode } from '@/types';

interface FollowUpQuestionsProps {
  followUpQuestions?: string[];
  animate: boolean;
  loading?: boolean;
  executeSearch: ExecuteKimSearch;
  questionMode: KimMode;
}

interface FollowUpQuestionsContentProps {
  followUpQuestions: FollowUpQuestionsProps['followUpQuestions'];
  animate: FollowUpQuestionsProps['animate'];
  onQuestionClick: (question: string) => void;
}

const FollowUpQuestionsContent = ({ followUpQuestions, animate, onQuestionClick }: FollowUpQuestionsContentProps) => {
  if (!followUpQuestions?.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded px-4 py-3 shadow">
      {followUpQuestions.map((question, index) => (
        <div
          className={`transform rounded px-2 transition-all duration-500 ease-out hover:bg-gray-100 ${
            animate ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
          }`}
          style={{ transitionDelay: `${index * 100}ms` }}
          key={question}
        >
          <div>
            <button
              className="flex w-full items-center justify-between rounded py-1 text-left text-sm"
              onClick={() => onQuestionClick(question)}
            >
              <p className="mr-3">{question}</p>
              <FontAwesomeIcon icon={faPlus} className="text-brandSecondary pb-1" />
            </button>
            <div className="flex h-px flex-1 bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
};

const FollowUpQuestions = ({
  followUpQuestions,
  animate,
  loading,
  executeSearch,
  questionMode,
}: FollowUpQuestionsProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const onQuestionClick = (question: string) => {
    searchParams.delete('threadId');
    navigate(`${location.pathname}?${searchParams.toString()}`);
    executeSearch({ question, questionMode });
    trackCustomEvent(TrackingEvents.KimFollowUpQuestion);
  };

  const hasNoContent = !loading && !followUpQuestions?.length;

  if (hasNoContent) {
    return null;
  }

  return (
    <>
      <div className="mx-2 mt-3 mb-5 flex items-center gap-3">
        <MessageCircleQuestion className="text-brandSecondary mb-0.5 size-5" />

        <h2 className="text-sm font-semibold text-gray-800">Follow up questions</h2>
      </div>

      <SkeletonOrComponent
        isLoading={loading}
        component={
          <FollowUpQuestionsContent
            animate={animate}
            followUpQuestions={followUpQuestions}
            onQuestionClick={onQuestionClick}
          />
        }
      />
    </>
  );
};

export default FollowUpQuestions;
